import { z } from "zod";

import { LessonIdSchema } from "./identifiers.js";
import { VisualOperationSchema } from "./visual-teaching.js";

export const LessonPhaseSchema = z.enum([
  "explain",
  "model",
  "guided_practice",
  "diagnose",
  "fade_scaffold",
  "independent_transfer",
  "summary",
  "retention",
]);
export type LessonPhase = z.infer<typeof LessonPhaseSchema>;

export const SourceReviewStatusSchema = z.enum([
  "ocr_candidate",
  "trusted_visual_reviewed",
  "canonical_approved",
  "rejected",
]);

export const SourceRefSchema = z
  .object({
    sourceRefVersion: z.literal("2"),
    sourceId: z.string().min(1),
    bookId: z.string().min(1),
    sourceName: z.string().min(1),
    sourcePdfSha256: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
    pdfPage: z.number().int().positive().nullable(),
    printedPage: z.number().int().positive().nullable(),
    mappingStatus: z.enum(["exact", "ambiguous", "blocked"]),
    reviewArtifactPath: z.string().min(1),
    reviewArtifactSha256: z.string().regex(/^[a-f0-9]{64}$/),
    imagePath: z.string().min(1).nullable(),
    imageSha256: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
    imageAssetStatus: z.enum(["available", "missing", "ambiguous", "not_applicable"]),
    reviewStatus: SourceReviewStatusSchema,
    reviewedBy: z.string().min(1).nullable(),
    reviewedAt: z.string().datetime({ offset: true }).nullable(),
    contentRelationship: z
      .enum([
        "direct_or_derived_textbook_basis",
        "context_only_for_mathematics_supplement",
      ])
      .optional(),
    bbox: z
      .object({
        x: z.number().nonnegative(),
        y: z.number().nonnegative(),
        width: z.number().positive(),
        height: z.number().positive(),
      })
      .optional(),
  })
  .superRefine((source, ctx) => {
    if (source.reviewStatus !== "canonical_approved") return;

    const requirements: Array<[boolean, string, keyof typeof source]> = [
      [source.sourcePdfSha256 !== null, "Canonical sources require the original PDF SHA-256", "sourcePdfSha256"],
      [source.pdfPage !== null, "Canonical sources require an explicit PDF page", "pdfPage"],
      [source.printedPage !== null, "Canonical sources require an explicit printed page", "printedPage"],
      [source.mappingStatus === "exact", "Canonical page mapping must be exact", "mappingStatus"],
      [source.imageAssetStatus === "available", "Canonical sources require a rendered page image", "imageAssetStatus"],
      [source.imagePath !== null, "Canonical sources require a rendered page image path", "imagePath"],
      [source.imageSha256 !== null, "Canonical sources require a rendered page image SHA-256", "imageSha256"],
      [source.reviewedBy !== null, "Canonical sources require an identified human reviewer", "reviewedBy"],
      [source.reviewedAt !== null, "Canonical sources require a review timestamp", "reviewedAt"],
      [source.bbox !== undefined, "Canonical sources require a concrete reviewed page region", "bbox"],
    ];

    for (const [satisfied, message, path] of requirements) {
      if (!satisfied) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: [path],
        });
      }
    }
  });
export type SourceRef = z.infer<typeof SourceRefSchema>;

export const ContentProvenanceSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("textbook_source_approved"),
  }),
  z.object({
    kind: z.literal("mathematics_teaching_supplement"),
    adjudicationId: z.string().regex(/^OMA-[A-Za-z0-9._-]+$/u),
    manifestPath: z.string().min(1),
    manifestSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    mayClaimVerbatimTextbookSource: z.literal(false),
  }),
]);
export type ContentProvenance = z.infer<typeof ContentProvenanceSchema>;

export const KnowledgeAtomSchema = z.object({
  id: z.string().min(1),
  kind: z.enum([
    "definition",
    "derivation",
    "condition",
    "worked_example",
    "misconception",
    "method",
  ]),
  title: z.string().min(1),
  canonicalText: z.string().min(1),
  mathStatements: z.array(z.string()).default([]),
  sourceRefs: z.array(SourceRefSchema).min(1),
  contentProvenance: ContentProvenanceSchema.optional(),
  approvalStatus: z.enum(["draft", "approved", "blocked"]),
});
export type KnowledgeAtom = z.infer<typeof KnowledgeAtomSchema>;

export const SemanticRelationRequirementSchema = z.object({
  subjectTerms: z.array(z.string().min(1)).min(1),
  objectTermGroups: z.array(z.array(z.string().min(1)).min(1)).min(1),
  parallelObjectTerms: z.array(z.string().min(1)).min(1).optional(),
  incompatibleObjectTerms: z.array(z.string().min(1)).default([]),
  satisfiesClaims: z.array(z.string().min(1)).min(1),
});
export type SemanticRelationRequirement = z.infer<
  typeof SemanticRelationRequirementSchema
>;

export const VerifierSpecSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("exact"),
    accepted: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    kind: z.literal("numeric"),
    expected: z.number(),
    tolerance: z.number().positive().default(1e-9),
  }),
  z.object({
    kind: z.literal("expression"),
    expectedLatex: z.string().min(1),
    assumptions: z.array(z.string()).default([]),
  }),
  z.object({
    kind: z.literal("set"),
    expectedMembers: z.array(z.string()),
    orderMatters: z.boolean().default(false),
  }),
  z.object({
    kind: z.literal("structured"),
    requiredClaims: z.array(z.string().min(1)).min(1),
    requiredClaimAlternatives: z
      .array(z.array(z.string().min(1)).min(2))
      .optional(),
    alternativeRequiredClaimSets: z
      .array(z.array(z.string().min(1)).min(1))
      .min(2)
      .optional(),
    semanticRelations: z
      .array(SemanticRelationRequirementSchema)
      .min(1)
      .optional(),
    forbiddenClaims: z.array(z.string().min(1)).default([]),
  }),
]);
export type VerifierSpec = z.infer<typeof VerifierSpecSchema>;

export const LessonActivitySchema = z
  .object({
    id: z.string().min(1),
    phase: LessonPhaseSchema,
    title: z.string().min(1),
    teacherIntent: z.string().min(1),
    studentPrompt: z.string().min(1).optional(),
    knowledgeAtomIds: z.array(z.string()).min(1),
    verifier: VerifierSpecSchema.optional(),
    teacherDemonstrationVerifier: VerifierSpecSchema.optional(),
    independence: z.enum(["teacher_led", "guided", "independent"]),
    hints: z.array(z.string()).max(3).default([]),
    contentProvenance: ContentProvenanceSchema.optional(),
  })
  .superRefine((activity, ctx) => {
    if (
      activity.teacherDemonstrationVerifier !== undefined &&
      (activity.independence !== "teacher_led" ||
        (activity.phase !== "explain" && activity.phase !== "model"))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "A teacher demonstration verifier is only valid for teacher-led explain or model activities",
        path: ["teacherDemonstrationVerifier"],
      });
    }
    if (
      activity.teacherDemonstrationVerifier !== undefined &&
      activity.teacherDemonstrationVerifier.kind !== "structured"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A teacher demonstration verifier must be structured",
        path: ["teacherDemonstrationVerifier"],
      });
    }
  });
export type LessonActivity = z.infer<typeof LessonActivitySchema>;

export const LessonUnitSchema = z
  .object({
    id: LessonIdSchema,
    familyId: z.string().min(1),
    title: z.string().min(1),
    topic: z.string().min(1),
    estimatedMinutes: z.number().int().min(25).max(35),
    ageRange: z.tuple([z.number().int().min(10), z.number().int().max(15)]),
    approvalStatus: z.enum(["draft", "approved", "blocked"]),
    approvalReason: z.string().min(1),
    knowledgeAtoms: z.array(KnowledgeAtomSchema).min(1),
    activities: z.array(LessonActivitySchema).min(4),
    retentionActivity: LessonActivitySchema.optional(),
    sourceCoverage: z.object({
      expectedSourceIds: z.array(z.string().min(1)).min(1),
      coveredSourceIds: z.array(z.string().min(1)),
      coverageRatio: z.number().min(0).max(1),
    }),
  })
  .strict()
  .superRefine((lesson, ctx) => {
    const atomIds = new Set(lesson.knowledgeAtoms.map((atom) => atom.id));
    const allActivities =
      lesson.retentionActivity === undefined
        ? lesson.activities
        : [...lesson.activities, lesson.retentionActivity];
    for (const activity of allActivities) {
      for (const atomId of activity.knowledgeAtomIds) {
        if (!atomIds.has(atomId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Activity ${activity.id} references missing atom ${atomId}`,
            path: ["activities"],
          });
        }
      }
    }
    if (
      lesson.approvalStatus === "approved" &&
      lesson.knowledgeAtoms.some((atom) => atom.approvalStatus !== "approved")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Approved lessons may only contain approved knowledge atoms",
        path: ["knowledgeAtoms"],
      });
    }
    if (lesson.approvalStatus === "approved") {
      const expectedSourceIds = new Set(lesson.sourceCoverage.expectedSourceIds);
      const coveredSourceIds = new Set(lesson.sourceCoverage.coveredSourceIds);
      const atomSourceIds = new Set(
        lesson.knowledgeAtoms.flatMap((atom) =>
          atom.sourceRefs.map((source) => source.sourceId),
        ),
      );

      if (
        lesson.sourceCoverage.coverageRatio !== 1 ||
        [...expectedSourceIds].some((sourceId) => !coveredSourceIds.has(sourceId))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Approved lessons require complete source coverage",
          path: ["sourceCoverage"],
        });
      }
      for (const sourceId of expectedSourceIds) {
        if (!atomSourceIds.has(sourceId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Expected source ${sourceId} is not attached to a knowledge atom`,
            path: ["sourceCoverage", "expectedSourceIds"],
          });
        }
      }
      for (const sourceId of atomSourceIds) {
        if (!expectedSourceIds.has(sourceId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Knowledge-atom source ${sourceId} is missing from source coverage`,
            path: ["sourceCoverage", "expectedSourceIds"],
          });
        }
      }

      const explainIndex = lesson.activities.findIndex(
        (activity) => activity.phase === "explain",
      );
      const modelIndex = lesson.activities.findIndex(
        (activity) => activity.phase === "model",
      );
      const firstIndependentIndex = lesson.activities.findIndex(
        (activity) => activity.independence === "independent",
      );
      if (
        explainIndex !== 0 ||
        modelIndex <= explainIndex ||
        firstIndependentIndex <= modelIndex
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Approved lessons must explain first, model the method next, and only then enter independent work",
          path: ["activities"],
        });
      }
      if (lesson.activities.some((activity) => activity.phase === "retention")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Delayed retention must not be part of the immediate classroom activity sequence",
          path: ["activities"],
        });
      }
      const retention = lesson.retentionActivity;
      if (retention === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Approved lessons require a separate delayed retention activity",
          path: ["retentionActivity"],
        });
      } else {
        if (
          retention.phase !== "retention" ||
          retention.independence !== "independent" ||
          retention.studentPrompt === undefined ||
          retention.verifier === undefined ||
          retention.hints.length > 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Delayed retention must be independent, hint-free, and executable",
            path: ["retentionActivity"],
          });
        }
        if (lesson.activities.some((activity) => activity.id === retention.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Delayed retention activity ID must be unique",
            path: ["retentionActivity", "id"],
          });
        }
      }
      allActivities.forEach((activity, index) => {
        if (activity.studentPrompt === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Approved activity ${activity.id} requires an explicit student prompt`,
            path:
              index < lesson.activities.length
                ? ["activities", index, "studentPrompt"]
                : ["retentionActivity", "studentPrompt"],
          });
        }
        if (activity.verifier === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Approved activity ${activity.id} requires an executable verifier`,
            path:
              index < lesson.activities.length
                ? ["activities", index, "verifier"]
                : ["retentionActivity", "verifier"],
          });
        }
        if (
          activity.independence === "independent" &&
          activity.hints.length > 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Independent activity ${activity.id} may not contain hints`,
            path:
              index < lesson.activities.length
                ? ["activities", index, "hints"]
                : ["retentionActivity", "hints"],
          });
        }
      });
    }
  });
export type LessonUnit = z.infer<typeof LessonUnitSchema>;

export const CurriculumPackageSchema = z.object({
  packageId: z.string().min(1),
  version: z.string().min(1),
  title: z.string().min(1),
  sourceRightsStatus: z.enum(["unverified", "authorized", "private_family_use"]),
  canonicalBaselineId: z.string().min(1),
  lessons: z.array(LessonUnitSchema),
});
export type CurriculumPackage = z.infer<typeof CurriculumPackageSchema>;

export const FinalAnswerSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("exact"),
    value: z.string().min(1).max(2_000),
  }),
  z.object({
    kind: z.literal("numeric"),
    value: z.number().finite(),
  }),
  z.object({
    kind: z.literal("expression"),
    latex: z.string().min(1).max(2_000),
  }),
  z.object({
    kind: z.literal("set"),
    members: z.array(z.string().min(1).max(300)).max(100),
  }),
  z.object({
    kind: z.literal("structured"),
    claims: z.array(z.string().min(1).max(1_000)).min(1).max(40),
  }),
]);
export type FinalAnswer = z.infer<typeof FinalAnswerSchema>;

export const StudentAnswerPayloadSchema = z
  .object({
    idempotencyKey: z.string().uuid(),
    activityId: z.string().min(1),
    finalAnswer: FinalAnswerSchema,
    explanation: z.string().max(4_000).optional(),
    confidence: z.enum(["unsure", "somewhat_sure", "sure"]).optional(),
    submittedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type StudentAnswerPayload = z.infer<typeof StudentAnswerPayloadSchema>;

export const BoardOperationSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("write"),
    blockId: z.string().min(1),
    format: z.enum(["text", "latex"]),
    content: z.string().min(1).max(4_000),
  }),
  z.object({
    type: z.literal("highlight"),
    blockId: z.string().min(1),
    tone: z.enum(["focus", "correct", "caution"]),
  }),
  z.object({
    type: z.literal("clear"),
    blockId: z.string().min(1),
  }),
]);

export const TeacherTurnEnvelopeSchema = z.object({
  schemaVersion: z.literal("1"),
  turnId: z.string().uuid(),
  sessionId: z.string().uuid(),
  activityId: z.string().min(1),
  phase: LessonPhaseSchema,
  teacherText: z.string().min(1).max(4_000),
  speechText: z.string().min(1).max(4_000).optional(),
  boardOperations: z.array(BoardOperationSchema).max(12),
  visualOperations: z.array(VisualOperationSchema).max(8).optional(),
  question: z.string().min(1).max(1_000).optional(),
  expectedResponse: z
    .object({
      kind: z.enum(["none", "text", "math", "choice", "ordered_steps", "table"]),
      description: z.string().max(800),
    })
    .optional(),
  stateIntent: z.enum(["stay", "advance", "clarify", "pause", "complete"]),
  citedKnowledgeAtomIds: z.array(z.string()).min(1),
  provider: z.object({
    id: z.string().min(1),
    model: z.string().min(1),
    requestId: z.string().min(1).optional(),
  }),
});
export type TeacherTurnEnvelope = z.infer<typeof TeacherTurnEnvelopeSchema>;

export const VerificationResultSchema = z.object({
  outcome: z.enum(["correct", "incorrect", "uncertain"]),
  normalizedAnswer: z.string(),
  reasons: z.array(z.string()).max(8),
  matchedClaims: z.array(z.string()).default([]),
  missingClaims: z.array(z.string()).default([]),
  forbiddenClaims: z.array(z.string()).default([]),
  mayAdvance: z.boolean(),
  needsTeacherClarification: z.boolean(),
  verifier: z.string().min(1),
});
export type VerificationResult = z.infer<typeof VerificationResultSchema>;

export const SessionEventSchema = z.object({
  eventId: z.string().uuid(),
  sessionId: z.string().uuid(),
  householdId: z.string().uuid(),
  childId: z.string().uuid(),
  sequence: z.number().int().nonnegative(),
  type: z.enum([
    "session_started",
    "teacher_turn_committed",
    "student_answer_submitted",
    "answer_verified",
    "activity_advanced",
    "session_paused",
    "session_resumed",
    "teacher_fallback_authorized",
    "teacher_fallback_consumed",
    "mastery_recorded",
    "session_completed",
  ]),
  occurredAt: z.string().datetime({ offset: true }),
  idempotencyKey: z.string().uuid().optional(),
  payload: z.record(z.unknown()),
});
export type SessionEvent = z.infer<typeof SessionEventSchema>;

export const SessionSnapshotSchema = z.object({
  sessionId: z.string().uuid(),
  householdId: z.string().uuid(),
  childId: z.string().uuid(),
  lessonId: LessonIdSchema,
  status: z.enum(["active", "paused", "completed"]),
  phase: LessonPhaseSchema,
  activityIndex: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  lastEventSequence: z.number().int().nonnegative(),
  updatedAt: z.string().datetime({ offset: true }),
});
export type SessionSnapshot = z.infer<typeof SessionSnapshotSchema>;

export const MasteryEvidenceSchema = z.object({
  evidenceId: z.string().uuid(),
  sessionId: z.string().uuid(),
  childId: z.string().uuid(),
  lessonId: LessonIdSchema,
  activityId: z.string().min(1),
  attemptNumber: z.number().int().positive(),
  kind: z.enum(["guided", "independent_transfer", "structured_retell", "retention"]),
  studentAnswer: StudentAnswerPayloadSchema,
  verification: VerificationResultSchema,
  teacherHelpAfterPrompt: z.boolean(),
  recordedAt: z.string().datetime({ offset: true }),
});
export type MasteryEvidence = z.infer<typeof MasteryEvidenceSchema>;

export const RetentionTaskSchema = z.object({
  taskId: z.string().uuid(),
  childId: z.string().uuid(),
  lessonId: LessonIdSchema,
  sourceActivityId: z.string().min(1),
  sourceEvidenceId: z.string().uuid().optional(),
  dueAt: z.string().datetime({ offset: true }),
  status: z.enum(["scheduled", "available", "completed", "expired"]),
  latestEvidenceId: z.string().uuid().optional(),
  completedEvidenceId: z.string().uuid().optional(),
});
export type RetentionTask = z.infer<typeof RetentionTaskSchema>;

export const HardFailureCodeSchema = z.enum([
  "source_mismatch",
  "teacher_math_error",
  "answer_leak",
  "system_substitution",
  "false_completion",
  "cross_tenant_access",
  "answer_loss",
  "unrecoverable_session",
  "internal_text_leak",
]);
export type HardFailureCode = z.infer<typeof HardFailureCodeSchema>;
